import React, { useState } from "react";
import { Box, Typography, Button, Chip, FormControlLabel, Switch, Tooltip, TextField, MenuItem } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import styles from './index.module.css';
import CustomTextField from "../../CustomTextField";
import CustomDropdown from "../../CustomDropdownWithPlus";
import TextArea from "../../TextArea";
import CustomTabSwitch from '../../CustomTabSwitch'
import CounterInput from '../../CounterInput'
import DecorationCharges from '../DecorationCharges';
import DecorationMatrixTable from '../../CustomTable';

const chargeTypeOptions = [
    { id: 'Run Charge', name: 'Run Charge' },
    { id: 'Fixed Charge', name: 'Fixed Charge' }
]
const runChargeOptions = [
    { value: 'Per Item', label: 'Per Item' },
    { value: 'Table', label: 'Table' }
];

const fixedChargeOptions = [
    { value: 'List', label: 'List' },
    { value: 'Table', label: 'Table' }
];
const REQUIRES_PROOF_TOOLTIP =
    "When enabled, this charge requires the proof workflow to be completed before the related purchase order can proceed. Turn it off only for charges that do not require artwork/proof approval.";

const RequiresProofLabel = () => (
    <Box component="span" display="inline-flex" alignItems="center" gap={0.5}>
        <span>Requires Proof</span>
        <Tooltip title={REQUIRES_PROOF_TOOLTIP} arrow placement="top">
            <InfoOutlinedIcon
                sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
                aria-label="What Requires Proof means"
            />
        </Tooltip>
    </Box>
);


const DecoratorMatrixForm = ({ index, decorationInfo, handleUpdateDecoration, onCopy,
    onDelete, canDeleteCharge, onArchiveToggle, isArchivingCharge, isEditMode, onOpenProductionRules, onEdit, onSave, onCancel, canStartEdit, isSaving, isEliteDecorator, productionDepartments = []
}) => {
    const [errors, setErrors] = useState({});
    const handleRowChange = (value) => {
        let updatedRows = [...(decorationInfo.rowsData || [])];

        if (value > updatedRows.length) {
            const itemsToAdd = value - updatedRows.length;
            updatedRows = [
                ...updatedRows,
                ...Array(itemsToAdd).fill({ Unit: '' })
            ];
        } else if (value < updatedRows.length) {
            updatedRows = updatedRows.slice(0, value);
        }

        handleUpdateDecoration(index, { rowsData: updatedRows, rows: value });
    };

    const handleBreakdownChange = (value) => {
        let updatedColumns = [...(decorationInfo.columns || [])];

        if (value > decorationInfo.breakdown) {
            // Add new columns
            let previousEnd = 0;
            if (updatedColumns.length > 0) {
                const lastColumn = updatedColumns[updatedColumns.length - 1];
                if (typeof lastColumn.header === 'object' && lastColumn.header.end) {
                    previousEnd = lastColumn.header.end;
                }
            }
            for (let i = 0; i < (value - decorationInfo.breakdown); i++) {
                const start = parseInt(previousEnd) + 1;
                const end = parseInt(start) + 1;

                updatedColumns.push({
                    accessorKey: `Price ${decorationInfo.breakdown + i + 1}`,
                    header: { start, end },
                });

                previousEnd = end;
            }
        } else if (value < decorationInfo.breakdown) {
            // Remove columns
            updatedColumns = updatedColumns.slice(0, updatedColumns.length - (decorationInfo.breakdown - value));
        }

        handleUpdateDecoration(index, { columns: updatedColumns, breakdown: value });
    };
    const onFormChange = (name, value) => {
        handleUpdateDecoration(index, { [name]: value })
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        handleUpdateDecoration(index, { [name]: value });
    }

    const handleTextAreaChange = (e) => {
        const { value } = e.target;
        onFormChange('description', value);
    };

    const handleTabSwitchChange = (newValue) => {
        onFormChange('type', newValue);
    };

    const handleSelect = (event, name) => {
        const value = event.target.value;
        onFormChange(name, value);

        if (errors[name] && value) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }

        if (name === 'chargeType') {
            let breakdown = 1;
            let columns = [{ accessorKey: 'Unit', header: 'Unit' }];

            if (value === 'Run Charge') {
                breakdown = 5;
                let previousEnd = 0;
                for (let i = 0; i < breakdown; i++) {
                    const start = previousEnd + 1;
                    const end = start + 1;

                    columns.push({
                        accessorKey: `${start}-${end}`,
                        header: { start, end },
                    });

                    previousEnd = end;
                }
            } else {
                columns.push({
                    accessorKey: `Cost`,
                    header: 'Cost',
                });
            }

            handleUpdateDecoration(index, {
                chargeType: value,
                columns,
                type: 'Table',
                rows: 1,
                breakdown: value === 'Run Charge' ? 5 : 1
            });
        }
    };

    const shouldShowBreakdown = decorationInfo?.chargeType === 'Run Charge' && decorationInfo?.type === 'Table';

    return (
        // <Paper>
        <Box className={styles.formContainer}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6" fontWeight="600">
                        {!isEditMode ? `${decorationInfo?.chargeName} ${decorationInfo?.chargeType ? `(${decorationInfo?.chargeType})` : ''}` : decorationInfo?.chargeName}
                    </Typography>
                    {decorationInfo?.archived && (
                        <Chip label="Archived" size="small" color="warning" variant="outlined" />
                    )}
                </Box>
                <Box display="flex" gap={1}>
                    {!!decorationInfo?.id && !decorationInfo?.archived && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => onOpenProductionRules?.(decorationInfo)}
                        >
                            Production Rules
                        </Button>
                    )}
                    {!isEditMode && !decorationInfo?.archived && (
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            disabled={!canStartEdit}
                            onClick={() => onEdit?.(decorationInfo?.clientKey)}
                        >
                            Edit
                        </Button>
                    )}
                    {!isEditMode && !!decorationInfo?.id && canDeleteCharge && (
                        <>
                            <Button
                                variant="outlined"
                                color={decorationInfo?.archived ? "success" : "warning"}
                                size="small"
                                disabled={isArchivingCharge}
                                onClick={() => onArchiveToggle?.(decorationInfo, !decorationInfo?.archived)}
                            >
                                {isArchivingCharge
                                    ? (decorationInfo?.archived ? "Restoring..." : "Archiving...")
                                    : (decorationInfo?.archived ? "Restore" : "Archive")}
                            </Button>
                            {!decorationInfo?.archived && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    onClick={() => onDelete(index, decorationInfo)}
                                >
                                    Delete
                                </Button>
                            )}
                        </>
                    )}
                    {isEditMode && <>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={() => onCopy(index, decorationInfo)}
                        >
                            Copy
                        </Button>
                        {canDeleteCharge && (
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() => onDelete(index, decorationInfo)}
                            >
                                Delete
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onCancel}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={onSave}
                            disabled={isSaving}
                        >
                            Save
                        </Button>
                    </>}
                </Box>
            </Box>
            <Box>
                {isEditMode && <Box display="flex" gap={3} >
                    <Box className="field">
                        <CustomTextField
                            label="Charge Name"
                            name="chargeName"
                            placeholder="Enter Charge Name"
                            value={decorationInfo?.chargeName}
                            handleChange={handleInputChange}
                        />
                    </Box>
                    {decorationInfo?.chargeName && (
                        <Box className="field">
                            <CustomDropdown
                                label="Charge Type"
                                name="chargeType"
                                options={chargeTypeOptions}
                                handleSelect={handleSelect}
                                selectedValue={decorationInfo?.chargeType}
                            />
                        </Box>
                    )}
                    {
                        decorationInfo?.chargeType && <CustomTabSwitch
                            value={decorationInfo?.type}
                            onChange={(newValue) => handleTabSwitchChange(newValue)}
                            options={
                                decorationInfo?.chargeType === 'Run Charge'
                                    ? runChargeOptions
                                    : fixedChargeOptions
                            }
                        />
                    }

                    {decorationInfo?.chargeType && <CounterInput
                        label="Rows"
                        value={decorationInfo?.rows || 0}
                        onChange={(value) => handleRowChange(value)}
                    />}
                    {shouldShowBreakdown &&
                        <CounterInput
                            label="Breakdown"
                            value={decorationInfo?.breakdown || 0}
                            onChange={(value) => handleBreakdownChange(value)}
                        />}
                    {isEliteDecorator && !decorationInfo?.id && (
                      <Box sx={{ minWidth: 240 }}>
                        <TextField select fullWidth size="small" label="Production Department" required
                          value={decorationInfo?.productionDepartmentId || ''}
                          onChange={(e) => onFormChange('productionDepartmentId', e.target.value)}
                          helperText="Required for new internal Elite charges">
                          <MenuItem value="" disabled>Choose department</MenuItem>
                          {productionDepartments.map(dept => (
                            <MenuItem key={dept.id} value={dept.id}>{dept.name}</MenuItem>
                          ))}
                        </TextField>
                      </Box>
                    )}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={decorationInfo?.requiresProof !== false}
                                onChange={(event) => onFormChange('requiresProof', event.target.checked)}
                                inputProps={{ 'aria-label': 'Requires Proof' }}
                            />
                        }
                        label={<RequiresProofLabel />}
                    />
                </Box>}
                {!isEditMode && (
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <Typography variant="body2" color="text.secondary">
                            <RequiresProofLabel />
                        </Typography>
                        <Typography variant="body2" fontWeight="600">
                            {decorationInfo?.requiresProof !== false ? 'On' : 'Off'}
                        </Typography>
                    </Box>
                )}
                <Box className="introduction-field">
                    {isEditMode ?
                        <TextArea
                            name="description"
                            label="Description"
                            rows={2}
                            handleInputChange={handleTextAreaChange}
                            value={decorationInfo?.description || ''}
                        /> : <Typography>{decorationInfo?.description}</Typography>}
                </Box>
                {decorationInfo?.type === 'List' && <Box display="flex" gap={3}>

                    {isEditMode ? <Box className="field">
                        <CustomTextField
                            label="Increment"
                            name="increment"
                            placeholder="Enter increment"
                            value={decorationInfo?.increment}
                            handleChange={handleInputChange}
                        />
                    </Box> :
                        <Box sx={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center'
                        }}>
                            <Typography>
                                Increment
                            </Typography>
                            <Typography>{decorationInfo?.increment}</Typography>
                        </Box>
                    }
                    {isEditMode ? <Box className="field">
                        <CustomTextField
                            label="Units"
                            name="units"
                            placeholder="Enter units"
                            value={decorationInfo?.units}
                            handleChange={handleInputChange}
                        />
                    </Box> :
                        <Box sx={{
                            display: 'flex', flexDirection: 'column', alignItems: 'centre'
                        }}>
                            <Typography>
                                Units
                            </Typography>
                            <Typography>{decorationInfo?.units || ''}</Typography>
                        </Box>}
                </Box>}

                {decorationInfo?.chargeType ? decorationInfo?.type === 'Table' ? (
                    <DecorationMatrixTable

                        decorationInfo={decorationInfo}
                        isEditMode={isEditMode}
                        columns={decorationInfo?.columns || []}
                        data={decorationInfo?.rowsData || []}
                        onColumnsChange={(updatedColumns) => handleUpdateDecoration(index, { columns: updatedColumns })}
                        onDataChange={(updatedData) => handleUpdateDecoration(index, { rowsData: updatedData,rows:updatedData?.length||0 })}
                    />
                ) : (
                    <DecorationCharges
                        decorationInfo={decorationInfo}
                        isEditMode={isEditMode}
                        charges={decorationInfo?.charges || []}
                        onChargesUpdate={(updatedCharges) => handleUpdateDecoration(index, { charges: updatedCharges })}
                    />
                ) : null}


            </Box>
        </Box>
    );
};

export default React.memo(
    DecoratorMatrixForm,
    (previousProps, nextProps) =>
        previousProps.decorationInfo === nextProps.decorationInfo &&
        previousProps.isEditMode === nextProps.isEditMode &&
        previousProps.isEliteDecorator === nextProps.isEliteDecorator &&
        previousProps.productionDepartments === nextProps.productionDepartments &&
        previousProps.canStartEdit === nextProps.canStartEdit &&
        previousProps.canDeleteCharge === nextProps.canDeleteCharge &&
        previousProps.isArchivingCharge === nextProps.isArchivingCharge &&
        previousProps.isSaving === nextProps.isSaving
);
